import { HttpError } from "@microbeta/common-frontend";
import { sendToastMessage } from "@microbeta/ui";
import {
  type QueryKey,
  type QueryObserverOptions,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createContext,
  type Dispatch,
  type FunctionComponent,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../shared/hooks";
import { useAsyncStorageState } from "../../shared/hooks/useAsyncStorageState";
import { httpClient } from "../../shared/utils/httpClientInstance";
import * as AuthService from "./services/authService";

type AuthState = {
  idToken?: string;
  refreshToken?: string;
  displayName?: string;
  email?: string;
  expiresIn?: string;
  emailVerified?: boolean;
  userId?: string;
  signInMethod?: string;
  loginTime?: number;
};

const defaultAuthState: AuthState = {};

type AuthContextType = {
  authIsPending: boolean;
  authState: AuthState;
  setAuthState: Dispatch<SetStateAction<AuthState>>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (redirectUrl?: string) => void;
  signInWithOneTap: (credential: string) => Promise<void>;
  logout: () => void;
  requestResetPassword: (email: string) => Promise<void>;
  resetPassword: (oobCode: string, newPassword: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
};

const defaultAuthContext: AuthContextType = {
  authIsPending: true,
  authState: defaultAuthState,
  setAuthState: () => {},
  signInWithEmail: async () => {},
  signInWithGoogle: () => {},
  signInWithOneTap: async () => {},
  logout: () => {},
  requestResetPassword: async () => {},
  resetPassword: async () => {},
  signUp: async () => {},
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

// Pure helper; hoisted so it doesn't capture render closures and doesn't need
// to live inside `useCallback` dependency arrays.
const getErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (error instanceof AuthService.AuthServiceError) {
    return error.message;
  }
  return defaultMessage;
};

export const AuthProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { captureEvent, identifyUser, resetAnalytics } = useAnalytics();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [authState, setAuthState] = useAsyncStorageState(
    "authState",
    defaultAuthState
  );
  const [authIsPending, setAuthIsPending] = useState(true);
  const initialSetupCompleted = useRef(false);
  const refreshAttempts = useRef(0);
  const lastRefreshAttempt = useRef(0);
  const isRefreshing = useRef(false);

  // Keep a live ref to the current auth state so callbacks (`logout` etc.)
  // can read the latest values without having `authState` in their dependency
  // arrays. Without this, every auth state update creates new callback
  // identities and ripples into every consumer's effects/deps.
  const authStateRef = useRef(authState);
  authStateRef.current = authState;

  const logout = useCallback(() => {
    const current = authStateRef.current;
    captureEvent("user_logged_out", {
      user_id: current.userId,
      session_duration: Date.now() - (current.loginTime || Date.now()),
    });
    resetAnalytics();

    refreshAttempts.current = 0;
    lastRefreshAttempt.current = 0;
    isRefreshing.current = false;

    setAuthState(defaultAuthState);
    httpClient.clearAuthToken();
    queryClient.clear();
  }, [setAuthState, queryClient, captureEvent, resetAnalytics]);

  useEffect(() => {
    if (authIsPending && !initialSetupCompleted.current) {
      initialSetupCompleted.current = true;

      const setupInitialAuthState = async () => {
        try {
          if (authState.refreshToken && !isRefreshing.current) {
            const now = Date.now();
            const timeSinceLastAttempt = now - lastRefreshAttempt.current;

            // Circuit breaker: prevent too many refresh attempts
            if (refreshAttempts.current >= 3 && timeSinceLastAttempt < 30000) {
              console.warn("Too many refresh attempts, waiting before retry");
              setAuthState(defaultAuthState);
              httpClient.clearAuthToken();
              return;
            }

            // Reset attempts if enough time has passed
            if (timeSinceLastAttempt > 300000) {
              // 5 minutes
              refreshAttempts.current = 0;
            }

            isRefreshing.current = true;
            lastRefreshAttempt.current = now;
            refreshAttempts.current++;

            try {
              const newCredentials = await AuthService.refreshToken(
                authState.refreshToken
              );

              if (newCredentials) {
                // Reset attempts on success
                refreshAttempts.current = 0;
                setAuthState((prev) => ({
                  ...prev,
                  idToken: newCredentials.id_token,
                  refreshToken: newCredentials.refresh_token,
                  expiresIn: newCredentials.expires_in,
                  userId: newCredentials.user_id,
                }));
                httpClient.setAuthToken(newCredentials.id_token);
              } else {
                setAuthState(defaultAuthState);
                httpClient.clearAuthToken();
              }
            } catch (e) {
              console.error("Initial token refresh failed", e);
              setAuthState(defaultAuthState);
              httpClient.clearAuthToken();
            } finally {
              isRefreshing.current = false;
            }
          }
        } finally {
          setAuthIsPending(false);
        }
      };

      setupInitialAuthState();
    }
  }, [authIsPending, setAuthState, authState.refreshToken]);

  // Sync authState.idToken with httpClient
  useEffect(() => {
    if (authState.idToken) {
      httpClient.setAuthToken(authState.idToken);
    } else {
      httpClient.clearAuthToken();
    }
  }, [authState.idToken]);

  // Aggressively clear cache on ANY user ID switch (handles cross-tab account swapping)
  const previousUserId = useRef<string | undefined>(authState.userId);
  useEffect(() => {
    if (previousUserId.current !== authState.userId) {
      queryClient.clear();
      previousUserId.current = authState.userId;
    }
  }, [authState.userId, queryClient]);

  useEffect(() => {
    queryClient.setQueryDefaults([], {
      onError: (error: unknown) => {
        console.error("React Query caught a query error:", error);
        if (error instanceof HttpError && error.status === 401) {
          console.warn(
            "401 Unauthorized detected via React Query query. Initiating logout."
          );
          logout();
          sendToastMessage(
            t("auth.sessionExpired"),
            t("auth.sessionExpiredMessage"),
            "warning",
            0
          );
        }
      },
    } as Partial<
      QueryObserverOptions<unknown, unknown, unknown, unknown, QueryKey>
    >);
    queryClient.setMutationDefaults([], {
      onError: (error: unknown) => {
        console.error("React Query caught a mutation error:", error);
        if (error instanceof HttpError && error.status === 401) {
          console.warn(
            "401 Unauthorized detected via React Query mutation. Initiating logout."
          );
          logout();
          sendToastMessage(
            t("auth.sessionExpired"),
            t("auth.sessionExpiredMessage"),
            "warning",
            0
          );
        }
      },
    });
  }, [logout, queryClient, t]);

  // Track user identification when auth state changes
  useEffect(() => {
    if (authState.idToken && authState.userId) {
      identifyUser(authState.userId, {
        email: authState.email,
        display_name: authState.displayName,
        email_verified: authState.emailVerified,
        signin_method: authState.signInMethod || "unknown",
      });
    }
  }, [
    authState.idToken,
    authState.userId,
    authState.email,
    authState.displayName,
    authState.emailVerified,
    identifyUser,
    authState.signInMethod,
  ]);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const signInStartTime = Date.now();
      try {
        let recaptchaToken: string | undefined;
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("login");
        }
        const result = await AuthService.signIn(
          email,
          password,
          recaptchaToken
        );
        setAuthState({
          idToken: result.idToken,
          refreshToken: result.refreshToken,
          displayName: result.displayName,
          email: result.email,
          expiresIn: result.expiresIn,
          emailVerified: result.emailVerified,
          userId: result.localId,
          signInMethod: "email",
          loginTime: Date.now(),
        });
        httpClient.setAuthToken(result.idToken);

        captureEvent("user_signed_in", {
          method: "email",
          email_domain: result.email.split("@")[1],
          email_verified: result.emailVerified,
          signin_duration: Date.now() - signInStartTime,
        });
      } catch (error) {
        console.error("Sign in failed:", error);
        const errorMessage = getErrorMessage(
          error,
          t("auth.signInErrorMessage")
        );
        sendToastMessage(t("auth.signInError"), errorMessage, "error", 0);
        setAuthState(defaultAuthState);

        captureEvent("user_signin_failed", {
          method: "email",
          error_type:
            error instanceof AuthService.AuthServiceError
              ? error.code
              : "unknown",
          signin_duration: Date.now() - signInStartTime,
        });

        throw error;
      }
    },
    [executeRecaptcha, setAuthState, captureEvent, t]
  );

  const signInWithGoogle = useCallback(
    (redirectUrl?: string) => {
      captureEvent("user_signin_attempted", {
        method: "google",
      });

      if (redirectUrl) {
        const urlString =
          typeof redirectUrl === "string" ? redirectUrl : String(redirectUrl);
        sessionStorage.setItem("redirectUrl", urlString);
      } else {
        sessionStorage.removeItem("redirectUrl");
      }

      let authUrl = `${
        import.meta.env.VITE_AUTH_BASE_URL
      }/auth/google/initiate/web`;

      if (redirectUrl) {
        authUrl = `${authUrl}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
      }
      window.location.href = authUrl;
    },
    [captureEvent]
  );

  const signInWithOneTap = useCallback(
    async (credential: string) => {
      const signInStartTime = Date.now();
      try {
        const result = await AuthService.verifyOneTapCredential(credential);
        setAuthState({
          idToken: result.idToken,
          refreshToken: result.refreshToken,
          displayName: result.displayName,
          email: result.email,
          expiresIn: result.expiresIn,
          emailVerified: result.emailVerified ?? true,
          userId: result.localId,
          signInMethod: "google",
          loginTime: Date.now(),
        });
        httpClient.setAuthToken(result.idToken);

        captureEvent("user_signed_in", {
          method: "google.onetap",
          email_domain: result.email?.split("@")[1],
          email_verified: result.emailVerified ?? true,
          signin_duration: Date.now() - signInStartTime,
        });
      } catch (error) {
        console.error("One Tap Sign in failed:", error);
        const errorMessage = getErrorMessage(
          error,
          t("auth.signInErrorMessage")
        );
        sendToastMessage(t("auth.signInError"), errorMessage, "error", 0);
        setAuthState(defaultAuthState);

        captureEvent("user_signin_failed", {
          method: "google.onetap",
          error_type:
            error instanceof AuthService.AuthServiceError
              ? error.code
              : "unknown",
          signin_duration: Date.now() - signInStartTime,
        });

        throw error;
      }
    },
    [setAuthState, captureEvent, t]
  );

  const signUp = useCallback(
    async (email: string, password: string, fullName: string) => {
      const signUpStartTime = Date.now();
      try {
        let recaptchaToken: string | undefined;
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("signup");
        }
        const result = await AuthService.signUp(
          email,
          password,
          fullName,
          recaptchaToken
        );
        setAuthState({
          idToken: result.idToken,
          refreshToken: result.refreshToken,
          displayName: result.displayName,
          email: result.email,
          expiresIn: result.expiresIn,
          userId: result.localId,
          signInMethod: "email",
          loginTime: Date.now(),
        });
        httpClient.setAuthToken(result.idToken);

        captureEvent("user_signed_up", {
          method: "email",
          email_domain: result.email.split("@")[1],
          full_name: fullName,
          signup_duration: Date.now() - signUpStartTime,
        });
      } catch (error) {
        console.error("Sign up failed:", error);
        const errorMessage = getErrorMessage(
          error,
          t("auth.signUpErrorMessage")
        );
        sendToastMessage(t("auth.signUpError"), errorMessage, "error");

        captureEvent("user_signup_failed", {
          method: "email",
          error_type:
            error instanceof AuthService.AuthServiceError
              ? error.code
              : "unknown",
          signup_duration: Date.now() - signUpStartTime,
        });

        throw error;
      }
    },
    [executeRecaptcha, setAuthState, captureEvent, t]
  );

  const requestResetPassword = useCallback(
    async (email: string) => {
      try {
        let recaptchaToken: string | undefined;
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("password_reset");
        }
        await AuthService.requestPasswordReset(email, recaptchaToken);
      } catch (error) {
        console.error("Request password reset failed:", error);
        throw error;
      }
    },
    [executeRecaptcha]
  );

  const resetPassword = useCallback(
    async (oobCode: string, newPassword: string) => {
      try {
        await AuthService.resetPassword(oobCode, newPassword);
      } catch (error) {
        console.error("Reset password failed:", error);
        throw error;
      }
    },
    []
  );

  const contextValue = useMemo<AuthContextType>(
    () => ({
      authIsPending,
      authState,
      setAuthState,
      signInWithEmail,
      signInWithGoogle,
      signInWithOneTap,
      logout,
      requestResetPassword,
      resetPassword,
      signUp,
    }),
    [
      authIsPending,
      authState,
      setAuthState,
      signInWithEmail,
      signInWithGoogle,
      signInWithOneTap,
      logout,
      requestResetPassword,
      resetPassword,
      signUp,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
